import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useStoreSettings } from '../hooks/useStoreSettings';
import { useDispatch } from 'react-redux';
import { addToCart } from '../store/cartSlice';
import { FaShoppingCart } from 'react-icons/fa';
import { toast } from 'react-hot-toast';
import styles from './ProductCard.module.css';
import DOMPurify from 'dompurify';
import { discountLabel } from '../lib/discountLabel';

interface Product {
  id: string;
  name: string;
  price: number;
  old_price?: number;
  image_url?: string;
  slug: string;
  description?: string;
  short_description?: string;
}

interface ProductCardProps {
  product: Product;
  /** Index in the rendered list — used to mark the first card as LCP candidate. */
  index?: number;
  /** Kratak opis ispod cene; isključuje se u admin podešavanjima. */
  showDescription?: boolean;
}

const ProductCard: React.FC<ProductCardProps> = ({ product, index = 0, showDescription = true }) => {
  const { formatPrice, settings } = useStoreSettings();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  // Dodajemo console.log da vidimo podatke
  console.log('Product card short description:', product.short_description);
  const sanitizedShortDesc = product.short_description ? DOMPurify.sanitize(product.short_description) : '';
  console.log('Product card sanitized short description:', sanitizedShortDesc);
  console.log('Product old price:', product.old_price);

  // Provera da li je old_price zaista broj veći od nule
  const hasValidOldPrice = product.old_price && typeof product.old_price === 'number' && product.old_price > 0 && product.old_price > product.price;

  // Funkcija za čišćenje HTML-a i proveru vrednosti
  const cleanHtml = (html: string) => {
    if (!html) return '';
    // Create temporary div to strip HTML tags
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    return tempDiv.textContent || tempDiv.innerText || '';
  };
  
  // Check if the short description is just "0" after removing HTML
  const hasValidShortDescription = product.short_description && 
    (cleanHtml(product.short_description).trim() !== '0') && 
    (cleanHtml(product.short_description).trim() !== '');

  // Funkcija za dodavanje u korpu
  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault(); // Zaustavlja navigaciju na stranicu proizvoda
    e.stopPropagation(); // Zaustavlja dalje propagacije događaja
    
    dispatch(addToCart({
      id: product.id,
      name: product.name,
      price: product.price,
      image_url: product.image_url || '/placeholder-product.jpg',
      quantity: 1 // Dodajemo 1 kom
    }));
    
    toast.success(`${product.name} je dodat u korpu!`);
  };

  return (
    <Link to={`/p/${product.slug}`} className={styles.productCard}>
      <div className={styles.imageContainer}>
        <img
          src={product.image_url || '/placeholder-product.jpg'}
          srcSet={
            product.image_url && /\.jpe?g$/i.test(product.image_url)
              ? `${product.image_url.replace(/\.(jpe?g)$/i, '-400.$1')} 400w, ${product.image_url} 1000w`
              : undefined
          }
          alt={product.name}
          width={400}
          height={400}
          sizes="(max-width: 600px) 50vw, (max-width: 1024px) 33vw, 220px"
          loading={index < 3 ? 'eager' : 'lazy'}
          fetchPriority={index === 0 ? 'high' : undefined}
          decoding="async"
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            target.onerror = null;
            target.src = '/placeholder-product.jpg';
          }}
        />
        {hasValidOldPrice && (
          <span className={styles.discountBadge}>
            {Math.round(((product.old_price! - product.price) / product.old_price!) * 100)}%{' '}
            {discountLabel(settings?.language)}
          </span>
        )}
        
        {/* Dugme za dodavanje u korpu */}
        <button 
          className={styles.addToCartButton}
          onClick={handleAddToCart}
        >
          {FaShoppingCart({ className: styles.cartIcon })}
          Dodaj u korpu
        </button>
      </div>
      <div className={styles.productInfo}>
        <h3>{product.name}</h3>
        <div className={styles.priceContainer}>
          <span className={styles.price}>{formatPrice(product.price)}</span>
          {hasValidOldPrice && (
            <>
              <span className={styles.oldPrice}>
                {formatPrice(product.old_price!)}
              </span>
              <span className={styles.discount}>
                {Math.round(((product.old_price! - product.price) / product.old_price!) * 100)}% popusta
              </span>
            </>
          )}
        </div>
        {showDescription && hasValidShortDescription && (
          <div className={styles.descriptionContent}>
            <div 
              dangerouslySetInnerHTML={{ 
                __html: DOMPurify.sanitize(product.short_description || '')
              }} 
            />
          </div>
        )}
      </div>
    </Link>
  );
};

export default ProductCard; 